import { z } from "zod";
import { match } from "ts-pattern";
import { capitalize } from "@/utils/string";

export const ProjectType = z.enum(["android", "android_tv", "browser", "ios", "api", "stb"]);

export type ProjectTypeValue = z.infer<typeof ProjectType>;
export type PlatformOption = { id: ProjectTypeValue; label: string; available: boolean };
type PlatformType = ProjectTypeValue | undefined;

export const formatPlatformName = (type: string): string =>
  match(type)
    .with("android", () => "Android")
    .with("android_tv", () => "Android TV")
    .with("browser", () => "Browser")
    .with("ios", () => "iOS")
    .with("api", () => "API")
    .otherwise(() => capitalize(type));

// Every type listed, not `.otherwise()`: a type added to `ProjectType` then fails the build
// until someone decides which side it falls on.
export const projectKind = (type: ProjectTypeValue): "API" | "APP" =>
  match(type)
    .with("api", () => "API" as const)
    .with("android", "android_tv", "browser", "ios", "stb", () => "APP" as const)
    .exhaustive();

export const isPlatform = (platform: PlatformType, ...platformsToCheck: ProjectTypeValue[]): boolean => {
  if (!platform) return false;
  return platformsToCheck.includes(platform);
};

/** Platforms the backend accepts today, in the order the platform picker shows them. */
const projectTypeValues: ProjectTypeValue[] = ["android", "android_tv", "browser", "ios", "api"];

export const platforms = projectTypeValues.map((id) => ({
  id,
  label: formatPlatformName(id),
}));

export const platformOptions: PlatformOption[] = [
  ...platforms.map((platform) => ({ ...platform, available: true })),
  { id: "stb", label: "STB", available: false },
];
